import React, { FC, PropsWithChildren, useState } from "react";

import { useTranslation } from "react-i18next";
import IconButton from "@mui/material/IconButton";

import { Close, EditPencil } from "@/assets/icons";
import { isABroker } from "@/shared/hooks";
import { StyledPendingChip } from "@/styles/styled";

import { StyledEditIcons, StyledFormPanel, StyledH5 } from "./styled";

export type FormPanelMode = "read" | "edit";

type FormPanelProps = {
    renderRead?: () => React.ReactElement;
    renderEdit?: (closeEdit: () => void) => void;
    title: string;
    subTitle?: string;
    pending?: boolean;
    mode: FormPanelMode;
    dataTestId?: string;
};

export const FormPanel: FC<PropsWithChildren<FormPanelProps>> = ({
    title,
    mode,
    renderRead,
    renderEdit,
    children,
    pending = false,
    subTitle,
    dataTestId,
}) => {
    const { t } = useTranslation();
    const [isEditable, setIsEditable] = useState(mode === "edit");

    const handleCloseEdit = () => {
        setIsEditable(false);
    };

    const renderIcon = () => {
        if (renderRead && renderEdit) {
            if (isEditable) {
                return (
                    <Close
                        data-testid="form_panel-button-close"
                        onClick={() => {
                            setIsEditable(!isEditable);
                        }}
                    />
                );
            }
            return (
                <StyledEditIcons>
                    {pending && <StyledPendingChip>{t("Common.Pending")}</StyledPendingChip>}
                    {!isABroker() && (
                        <IconButton
                            data-testid="form_panel-button-edit"
                            onClick={() => {
                                setIsEditable(!isEditable);
                            }}
                            aria-label={t("Common.Edit")}
                        >
                            <EditPencil />
                        </IconButton>
                    )}
                </StyledEditIcons>
            );
        }
        return null;
    };
    return (
        <StyledFormPanel $isEditable={isEditable} $notEditable={!renderEdit} data-testid={dataTestId}>
            <>
                <div className="d-flex align-center justify-space-between mb16">
                    <StyledH5>{title}</StyledH5>
                    {renderIcon()}
                </div>
                {subTitle && <div className="subTitle">{subTitle}</div>}
                {children}
                {!children && !isEditable && renderRead()}
                {!children && isEditable && renderEdit(handleCloseEdit)}
            </>
        </StyledFormPanel>
    );
};
